import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Popover,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { makeStyles } from '@mui/styles';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LinkIcon from '@mui/icons-material/Link';
import VerifiedIcon from '@mui/icons-material/Verified';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { grey, green, orange, red } from '@mui/material/colors';
import StarIcon from '@mui/icons-material/Star';
import { useSnackbar } from 'notistack';
import { copyTextToClipboard, validateEmail } from '../utils';
import validURL from '../utils/validators/url';
import { ContextStore } from '../store/contextStore';
import { CREATE, DataAPIContext, UPDATE } from '../api';
import DeleteIconButton from '../components/DeleteIconButton';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import useConfirmDialog from '../components/useConfirmDialog';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        marginTop: theme.spacing(2),
    },
    tableContainer: {
        boxShadow: 'none',
        border: 'none',
    },
    tableHead: {
        backgroundColor: 'transparent',
    },
    tableHeadCell: {
        fontWeight: 600,
        fontSize: '0.75rem',
        color: grey[700],
        textTransform: 'uppercase',
        padding: '8px 12px',
        border: 'none',
    },
    tableCell: {
        padding: '8px 12px',
        verticalAlign: 'middle',
        border: 'none',
    },
    contactValue: {
        fontWeight: 500,
        wordBreak: 'break-all',
    },
    typeChip: {
        fontWeight: 500,
        fontSize: '0.7rem',
    },
    tierChip: {
        fontWeight: 500,
        fontSize: '0.7rem',
    },
    switchCell: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    editField: {
        minWidth: '120px',
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: grey[500],
    },
}));

// Contact type constants
const CONTACT_TYPE_EMAIL = 0;
const CONTACT_TYPE_PHONE = 1;
const CONTACT_TYPE_URL = 2;

const CONTACT_TYPES = {
    [CONTACT_TYPE_EMAIL]: { label: 'Email', icon: EmailIcon, color: '#1976d2' },
    [CONTACT_TYPE_PHONE]: { label: 'Phone', icon: PhoneIcon, color: '#388e3c' },
    [CONTACT_TYPE_URL]: { label: 'URL', icon: LinkIcon, color: '#f57c00' },
};

// Tier constants
const TIER_PRIVATE = 0;
const TIER_OTA = 10;
const TIER_FAKE = 20;

const TIERS = {
    [TIER_PRIVATE]: { label: 'Private', color: green[600] },
    [TIER_OTA]: { label: 'OTA', color: orange[600] },
    [TIER_FAKE]: { label: 'Fake', color: red[600] },
};

const normalizePhoneDigits = (p) => (p ? String(p).replace(/[^0-9+]/g, '') : '');

const normalizeContactForDuplicateCheck = (type, contact) => {
    if (contact === null || contact === undefined) {
        return '';
    }
    const value = String(contact).trim();
    if (!value) {
        return '';
    }
    const contactType = Number(type);
    if (contactType === CONTACT_TYPE_EMAIL) {
        return value.toLowerCase();
    }
    if (contactType === CONTACT_TYPE_PHONE) {
        return normalizePhoneDigits(value);
    }
    return value.toLowerCase();
};

const duplicateContactMessage = (type) => {
    const contactType = Number(type);
    if (contactType === CONTACT_TYPE_EMAIL) {
        return 'This email address already exists in Contacts.';
    }
    if (contactType === CONTACT_TYPE_PHONE) {
        return 'This phone number already exists in Contacts.';
    }
    return 'This contact already exists in Contacts.';
};

const findDuplicateContact = (contacts, candidate, excludedContact) => {
    const candidateType = Number(candidate.type);
    const candidateValue = normalizeContactForDuplicateCheck(candidateType, candidate.contact);
    if (!candidateValue) {
        return null;
    }
    return contacts.find((contact) => {
        if (contact === excludedContact) {
            return false;
        }
        if (contact.id && candidate.id && String(contact.id) === String(candidate.id)) {
            return false;
        }
        if (Number(contact.type) !== candidateType) {
            return false;
        }
        return normalizeContactForDuplicateCheck(contact.type, contact.contact) === candidateValue;
    }) || null;
};

const EMPTY_CONTACT_MESSAGE = 'Please enter a contact value.';
const INVALID_PHONE_MESSAGE = 'Please enter a valid phone number.';

// Format check for a non-empty contact value. Phone numbers are validated by the
// backend (see checkPhoneOnServer) — the canonical check lives in `user/validate_phone`.
const contactFormatError = (type, value) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) {
        return '';
    }
    const contactType = Number(type);
    if (contactType === CONTACT_TYPE_EMAIL) {
        return validateEmail(trimmed) ? '' : 'Please enter a valid email address.';
    }
    if (contactType === CONTACT_TYPE_URL) {
        return validURL(trimmed) ? '' : 'Please enter a valid URL.';
    }
    return '';
};

const primaryEmailMatches = (userEmail, contactValue) =>
    userEmail &&
    contactValue &&
    String(userEmail).trim().toLowerCase() === String(contactValue).trim().toLowerCase();

const primaryPhoneMatches = (userPhone, contactValue) =>
    userPhone &&
    contactValue &&
    normalizePhoneDigits(userPhone) === normalizePhoneDigits(contactValue);

const contactPrimarySortKey = (c) => [
    c.tier ?? TIER_PRIVATE,
    c.sequence ?? 0,
    c.created_ts ? new Date(c.created_ts).getTime() : 0,
];

const nextPrimaryContact = (contacts, contactType) => {
    const candidates = contacts.filter(
        (c) => c.type === contactType && c.enabled !== false
    );
    if (candidates.length === 0) {
        return undefined;
    }
    return [...candidates].sort((a, b) => {
        const ka = contactPrimarySortKey(a);
        const kb = contactPrimarySortKey(b);
        for (let i = 0; i < ka.length; i += 1) {
            if (ka[i] !== kb[i]) {
                return ka[i] - kb[i];
            }
        }
        return 0;
    })[0];
};

const ContactTypeIcon = ({ type }) => {
    const typeInfo = CONTACT_TYPES[type] || CONTACT_TYPES[CONTACT_TYPE_EMAIL];
    const Icon = typeInfo.icon;
    return (
        <Tooltip title={typeInfo.label}>
            <Icon style={{ color: typeInfo.color, fontSize: '1.2rem' }} />
        </Tooltip>
    );
};

const TierChip = ({ tier, classes }) => {
    const tierInfo = TIERS[tier] || TIERS[TIER_PRIVATE];
    return (
        <Chip
            label={`Tier: ${tierInfo.label}`}
            size="small"
            className={classes.tierChip}
            style={{
                backgroundColor: tierInfo.color,
                color: 'white',
            }}
        />
    );
};

const LastUpdatedCell = ({ contact, classes }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleMouseEnter = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMouseLeave = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const updatedTs = contact.updated_ts ? moment.utc(contact.updated_ts).format('MMM D, YYYY HH:mm') : null;
    const createdTs = contact.created_ts ? moment.utc(contact.created_ts).format('MMM D, YYYY HH:mm') : null;

    return (
        <TableCell className={classes.tableCell} style={{ minWidth: 120 }}>
            {updatedTs ? (
                <>
                    <Typography
                        variant="body2"
                        sx={{ 
                            fontSize: '0.8rem', 
                            color: grey[600],
                            cursor: createdTs ? 'default' : 'default',
                        }}
                        onMouseEnter={createdTs ? handleMouseEnter : undefined}
                        onMouseLeave={createdTs ? handleMouseLeave : undefined}
                    >
                        {updatedTs} UTC
                    </Typography>
                    {createdTs && (
                        <Popover
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleMouseLeave}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            sx={{ pointerEvents: 'none' }}
                            disableRestoreFocus
                        >
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: grey[600] }}>
                                    <strong>Created:</strong> {createdTs} UTC
                                </Typography>
                            </Box>
                        </Popover>
                    )}
                </>
            ) : (
                <Typography variant="body2" color="textSecondary">—</Typography>
            )}
        </TableCell>
    );
};

const ContactRow = ({
    contact,
    index,
    isEditing,
    editData,
    onEdit,
    onSave,
    onCancel,
    onDelete,
    onPromoteToPrimary,
    onFieldChange,
    onToggleVerified,
    classes,
    readOnly,
    user,
    hasWritePerm,
    deleteDisabled,
    deleteDisabledTitle,
    validationError,
    validating,
    saveDisabled,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const typeInfo = CONTACT_TYPES[contact.type] || CONTACT_TYPES[CONTACT_TYPE_EMAIL];
    const isPrimary =
        (contact.type === CONTACT_TYPE_EMAIL && primaryEmailMatches(user?.email, contact.contact)) ||
        (contact.type === CONTACT_TYPE_PHONE && primaryPhoneMatches(user?.phone, contact.contact));
    const hasBackendId = contact.id && !String(contact.id).startsWith('new-');
    const canPromote = hasWritePerm && !isPrimary && hasBackendId && (contact.type === CONTACT_TYPE_EMAIL || contact.type === CONTACT_TYPE_PHONE) && contact.contact;

    return (
                <TableRow
                    sx={{
                        backgroundColor: 'white',
                        '&:hover': { backgroundColor: grey[50] },
                    }}
                >
                    {/* Sequence */}
                    <TableCell className={classes.tableCell} style={{ width: 50 }}>
                        <Typography variant="body2" color="textSecondary">
                            {contact.sequence ?? index + 1}
                        </Typography>
                    </TableCell>

                    {/* Type */}
                    <TableCell className={classes.tableCell} style={{ width: 100 }}>
                        {isEditing ? (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={editData.type ?? contact.type}
                                    onChange={(e) => onFieldChange('type', e.target.value)}
                                    size="small"
                                >
                                    {Object.entries(CONTACT_TYPES).map(([value, info]) => (
                                        <MenuItem key={value} value={parseInt(value)}>
                                            {info.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Box display="flex" alignItems="center" gap={1}>
                                <ContactTypeIcon type={contact.type} />
                                <Typography variant="body2">{typeInfo.label}</Typography>
                            </Box>
                        )}
                    </TableCell>

                    {/* Contact Value */}
                    <TableCell className={classes.tableCell}>
                        {isEditing ? (
                            <TextField
                                size="small"
                                fullWidth
                                value={editData.contact ?? contact.contact ?? ''}
                                onChange={(e) => onFieldChange('contact', e.target.value)}
                                placeholder="Contact value"
                                error={Boolean(validationError)}
                                helperText={validationError || (validating ? 'Checking the phone number…' : '')}
                                InputProps={{
                                    endAdornment: validating ? <CircularProgress size={16} color="primary" /> : null,
                                }}
                            />
                        ) : (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="body2" className={classes.contactValue}>
                                    {contact.contact || '—'}
                                </Typography>
                                {contact.contact && (
                                    <Tooltip title="Copy to clipboard">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                copyTextToClipboard(contact.contact);
                                                enqueueSnackbar('Copied to clipboard', { variant: 'success' });
                                            }}
                                            sx={{ padding: '2px' }}
                                        >
                                            <ContentCopyIcon sx={{ fontSize: '0.9rem', color: grey[500] }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        )}
                    </TableCell>

                    {/* Tier */}
                    <TableCell className={classes.tableCell} style={{ minWidth: 100 }}>
                        {isEditing ? (
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                <Select
                                    value={editData.tier ?? contact.tier ?? TIER_PRIVATE}
                                    onChange={(e) => onFieldChange('tier', e.target.value)}
                                    size="small"
                                >
                                    {Object.entries(TIERS).map(([value, info]) => (
                                        <MenuItem key={value} value={parseInt(value)}>
                                            {info.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <TierChip tier={contact.tier} classes={classes} />
                        )}
                    </TableCell>

                    {/* OTA */}
                    <TableCell className={classes.tableCell} style={{ minWidth: 120 }}>
                        {isEditing ? (
                            <RemoteAutocomplete
                                resource="ota"
                                urlSuffix={null}
                                getOptionLabel={(opt) => opt?.name ?? opt ?? ''}
                                size="small"
                                fullWidth
                                onChange={(e, value) => onFieldChange('ota', value)}
                                value={editData.ota ?? contact.ota ?? null}
                            />
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                {contact.ota?.name || '—'}
                            </Typography>
                        )}
                    </TableCell>

                    {/* Primary / Promote to Primary */}
                    {hasWritePerm && (
                        <TableCell className={classes.tableCell} style={{ width: 120 }}>
                            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                {isPrimary && (
                                    <Chip
                                        label="Primary"
                                        size="small"
                                        color="primary"
                                        sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                                    />
                                )}
                                {canPromote && (
                                    <Tooltip title="Promote to primary">
                                        <Chip
                                            label="Make primary"
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            icon={<StarIcon sx={{ fontSize: '0.9rem !important' }} />}
                                            onClick={() => onPromoteToPrimary(contact)}
                                            sx={{ 
                                                fontWeight: 500, 
                                                fontSize: '0.7rem',
                                                cursor: 'pointer',
                                                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        </TableCell>
                    )}

                    {/* Verified */}
                    <TableCell className={classes.tableCell} style={{ width: 80 }}>
                        <Box display="flex" alignItems="center" justifyContent="center">
                            {readOnly ? (
                                <Tooltip title={contact.verified ? 'Verified' : 'Not verified'}>
                                    <Box display="flex" alignItems="center">
                                        {contact.verified ? (
                                            <VerifiedIcon style={{ color: green[600], fontSize: '1.2rem' }} />
                                        ) : (
                                            <Typography variant="body2" color="textSecondary">—</Typography>
                                        )}
                                    </Box>
                                </Tooltip>
                            ) : (
                                <>
                                    <Tooltip title={contact.verified ? 'Verified' : 'Not verified'}>
                                        <Switch
                                            size="small"
                                            checked={isEditing ? (editData.verified ?? contact.verified ?? false) : (contact.verified ?? false)}
                                            onChange={(e) => {
                                                if (isEditing) {
                                                    onFieldChange('verified', e.target.checked);
                                                } else {
                                                    onToggleVerified(contact, e.target.checked);
                                                }
                                            }}
                                            color="success"
                                        />
                                    </Tooltip>
                                    {contact.verified && <VerifiedIcon style={{ color: green[600], fontSize: '1rem', marginLeft: 4 }} />}
                                </>
                            )}
                        </Box>
                    </TableCell>

                    {/* Last Updated */}
                    <LastUpdatedCell contact={contact} classes={classes} />

                    {/* Actions */}
                    {!readOnly && (
                        <TableCell className={classes.tableCell} style={{ width: 100 }}>
                            {isEditing ? (
                                <Box display="flex" gap={0.5}>
                                    <Tooltip title={saveDisabled ? validationError || 'Checking the phone number…' : 'Save'}>
                                        <span>
                                            <IconButton size="small" onClick={onSave} color="primary" disabled={saveDisabled}>
                                                <SaveIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    {contact._isUnsaved ? (
                                        <DeleteIconButton onClick={onCancel} />
                                    ) : (
                                        <Tooltip title="Cancel">
                                            <IconButton size="small" onClick={onCancel} color="default">
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            ) : (
                                <Box display="flex" gap={0.5}>
                                    <Tooltip title="Edit">
                                        <IconButton size="small" onClick={() => onEdit(contact)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <DeleteIconButton
                                        title={deleteDisabled ? deleteDisabledTitle || 'Delete' : 'Delete'}
                                        onClick={() => onDelete(contact)}
                                        disabled={deleteDisabled}
                                    />
                                </Box>
                            )}
                        </TableCell>
                    )}
                </TableRow>
    );
};

const UserContactList = ({ user, contacts = [], onUpdate, readOnly = false }) => {
    const [confirmDelete, confirmDeleteDialog] = useConfirmDialog();
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext);
    const { hasPerm } = React.useContext(ContextStore);
    const { enqueueSnackbar } = useSnackbar();
    const hasWritePerm = hasPerm('user', 'write');
    const [localContacts, setLocalContacts] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});
    const [editError, setEditError] = useState('');
    // Result of the canonical server-side phone check for the value being edited
    const [phoneCheck, setPhoneCheck] = useState({ value: null, checking: false, valid: true });
    const localContactsRef = useRef(localContacts);
    const skipContactsSyncRef = useRef(false);

    // Keep ref in sync so async closures always read the latest value
    useEffect(() => {
        localContactsRef.current = localContacts;
    }, [localContacts]);

    // Initialize local contacts sorted by sequence
    useEffect(() => {
        // Skip overwrite when handlePromoteToPrimary just set authoritative state
        if (skipContactsSyncRef.current) {
            skipContactsSyncRef.current = false;
            return;
        }
        const sorted = [...(contacts || [])].sort((a, b) => {
            // Unsaved contacts always appear last
            if (a._isUnsaved !== b._isUnsaved) return a._isUnsaved ? 1 : -1;
            return (a.type ?? 0) - (b.type ?? 0) || (a.sequence ?? 0) - (b.sequence ?? 0);
        });
        setLocalContacts(sorted);
    }, [contacts]);

    // Helper to update user with new contacts
    const updateUserContacts = useCallback((newContacts) => {
        if (onUpdate && user) {
            onUpdate({
                ...user,
                contacts: newContacts,
            });
        }
    }, [onUpdate, user]);

    // Canonical phone validation — same endpoint the primary phone field uses
    const checkPhoneOnServer = useCallback(async (phone) => {
        try {
            const res = await dataAPI(CREATE, 'user/validate_phone', { data: { phone } });
            return Boolean(res?.data?.status);
        } catch (e) {
            console.error('Error validating phone number', e);
            // Don't block the user because of a network/API failure
            return true;
        }
    }, [dataAPI]);

    const editingContact = React.useMemo(
        () => localContacts.find((c, idx) => (c.id || idx) === editingId) || null,
        [localContacts, editingId]
    );
    const editingType = editingContact ? Number(editData.type ?? editingContact.type) : null;
    const editingValue = editingContact ? String(editData.contact ?? editingContact.contact ?? '').trim() : '';

    // Debounced server-side check of the phone number being typed
    useEffect(() => {
        if (editingType !== CONTACT_TYPE_PHONE || !editingValue) {
            setPhoneCheck({ value: null, checking: false, valid: true });
            return undefined;
        }
        const tm = setTimeout(async () => {
            setPhoneCheck({ value: editingValue, checking: true, valid: true });
            const valid = await checkPhoneOnServer(editingValue);
            setPhoneCheck({ value: editingValue, checking: false, valid });
        }, 700);
        return () => clearTimeout(tm);
    }, [editingType, editingValue, checkPhoneOnServer]);

    const phoneValidationError = (type, value) => {
        if (Number(type) !== CONTACT_TYPE_PHONE) {
            return '';
        }
        const trimmed = String(value ?? '').trim();
        if (!trimmed || phoneCheck.checking || phoneCheck.value !== trimmed) {
            return '';
        }
        return phoneCheck.valid ? '' : INVALID_PHONE_MESSAGE;
    };

    // Toggle verified flag
    const handleToggleVerified = (contact, verified) => {
        const updated = localContacts.map((c) =>
            c === contact ? { ...c, verified } : c
        );
        setLocalContacts(updated);
        updateUserContacts(updated);
    };

    // Start editing a contact
    const handleEdit = (contact) => {
        setEditingId(contact.id || localContacts.indexOf(contact));
        setEditData({});
        setEditError('');
    };

    // Handle field change during editing
    const handleFieldChange = (field, value) => {
        setEditError('');
        setEditData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // Save edited contact
    const handleSave = async (contact) => {
        const candidate = {
            ...contact,
            ...editData,
        };
        const rejectWith = (message) => {
            setEditError(message);
            enqueueSnackbar(message, { variant: 'warning' });
        };

        const value = String(candidate.contact ?? '').trim();
        if (!value) {
            rejectWith(EMPTY_CONTACT_MESSAGE);
            return;
        }
        const formatError = contactFormatError(candidate.type, value);
        if (formatError) {
            rejectWith(formatError);
            return;
        }
        if (Number(candidate.type) === CONTACT_TYPE_PHONE) {
            let valid;
            if (phoneCheck.value === value && !phoneCheck.checking) {
                valid = phoneCheck.valid;
            } else {
                setPhoneCheck({ value, checking: true, valid: true });
                valid = await checkPhoneOnServer(value);
                setPhoneCheck({ value, checking: false, valid });
            }
            if (!valid) {
                rejectWith(INVALID_PHONE_MESSAGE);
                return;
            }
        }

        const duplicate = findDuplicateContact(localContacts, candidate, contact);
        if (duplicate) {
            const message = duplicateContactMessage(candidate.type);
            setEditError(message);
            enqueueSnackbar(message, { variant: 'warning' });
            return;
        }

        const savedData = { ...editData, contact: value };
        const updated = localContacts.map((c) => {
            if (c === contact || c.id === editingId) {
                const rest = { ...c };
                delete rest._isUnsaved; // Remove _isUnsaved flag on save
                return {
                    ...rest,
                    ...savedData,
                };
            }
            return c;
        });
        setLocalContacts(updated);

        // Check if we're updating a primary contact and need to update user.email or user.phone
        let userUpdates = {};
        if (editData.contact !== undefined) {
            const isPrimaryEmail = primaryEmailMatches(user?.email, contact.contact);
            const isPrimaryPhone = primaryPhoneMatches(user?.phone, contact.contact);
            
            if (isPrimaryEmail) {
                userUpdates.email = value;
            }
            if (isPrimaryPhone) {
                userUpdates.phone = value;
            }
        }

        // Update user with new contacts and any primary field changes
        if (onUpdate && user) {
            onUpdate({
                ...user,
                ...userUpdates,
                contacts: updated,
            });
        }

        setEditingId(null);
        setEditData({});
        setEditError('');
    };

    // Cancel editing
    const handleCancel = () => {
        // If cancelling a new contact that was never saved, delete it
        const editingContact = localContacts.find((c) => c.id === editingId);
        if (editingContact?._isUnsaved) {
            const updated = localContacts.filter((c) => c.id !== editingId);
            // Re-sequence remaining contacts
            const resequenced = updated.map((c, idx) => ({
                ...c,
                sequence: idx + 1,
            }));
            setLocalContacts(resequenced);
            updateUserContacts(resequenced);
        }
        setEditingId(null);
        setEditData({});
        setEditError('');
    };

    // Delete contact
    const handleDelete = async (contact) => {
        const emailContactCount = localContacts.filter((c) => c.type === CONTACT_TYPE_EMAIL).length;
        if (contact.type === CONTACT_TYPE_EMAIL && emailContactCount <= 1) {
            enqueueSnackbar('Cannot delete the only email address.', { variant: 'warning' });
            return;
        }

        if (!(await confirmDelete({ body: 'Are you sure you want to delete this contact?' }))) {
            return;
        }
        const updated = localContacts.filter((c) => c !== contact && c.id !== contact.id);
        const resequenced = updated.map((c, idx) => ({
            ...c,
            sequence: idx + 1,
        }));
        setLocalContacts(resequenced);

        const userUpdates = {};
        if (primaryEmailMatches(user?.email, contact.contact) && contact.type === CONTACT_TYPE_EMAIL) {
            const next = nextPrimaryContact(updated, CONTACT_TYPE_EMAIL);
            if (next?.contact) {
                userUpdates.email = next.contact;
            }
        }
        if (primaryPhoneMatches(user?.phone, contact.contact) && contact.type === CONTACT_TYPE_PHONE) {
            const next = nextPrimaryContact(updated, CONTACT_TYPE_PHONE);
            userUpdates.phone = next?.contact ?? '';
        }

        if (onUpdate && user) {
            const deletedIds = [...(user._deleted_contact_ids || [])];
            if (contact.id && !String(contact.id).startsWith('new-')) {
                deletedIds.push(contact.id);
            }
            onUpdate({
                ...user,
                ...userUpdates,
                contacts: resequenced,
                _deleted_contact_ids: deletedIds,
            });
        }
    };

    // Promote contact to primary
    const handlePromoteToPrimary = async (contact) => {
        if (!contact.contact || !contact.id || !user?.id) return;

        const confirmMessage = 'Primary contact will no longer be updated by booking systems. You will be responsible for managing the primary contact for this user.';

        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            const response = await dataAPI(UPDATE, 'user', {
                id: user.id,
                urlSuffix: `promote_contact_to_primary/${contact.id}/`,
                data: {},
            });

            if (response && (response.statusCode === 200 || response.statusCode === 201)) {
                // Use server response to get the updated contacts list
                const serverContacts = response.data?.contacts;
                if (serverContacts) {
                    const sorted = [...serverContacts].sort((a, b) => {
                        if (a._isUnsaved !== b._isUnsaved) return a._isUnsaved ? 1 : -1;
                        return (a.type ?? 0) - (b.type ?? 0) || (a.sequence ?? 0) - (b.sequence ?? 0);
                    });
                    setLocalContacts(sorted);
                }

                // Update the user's primary email or phone
                const userUpdates = {};
                if (contact.type === CONTACT_TYPE_EMAIL) {
                    userUpdates.email = response.data?.email || contact.contact;
                    userUpdates.freeze_email = true;
                } else if (contact.type === CONTACT_TYPE_PHONE) {
                    userUpdates.phone = response.data?.phone || contact.contact;
                    userUpdates.freeze_phone = true;
                }

                // Prevent useEffect from overwriting the authoritative state we just set
                skipContactsSyncRef.current = true;

                // Notify parent to re-render with updated user data from server
                if (onUpdate && user) {
                    onUpdate({
                        ...user,
                        ...userUpdates,
                        contacts: serverContacts || localContactsRef.current,
                    });
                }

                enqueueSnackbar('Contact promoted to primary successfully', { variant: 'success' });
            } else {
                enqueueSnackbar('Failed to promote contact to primary', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error promoting contact to primary:', error);
            enqueueSnackbar('Failed to promote contact to primary', { variant: 'error' });
        }
    };

    // Add new contact
    const handleAddContact = () => {
        const newContact = {
            id: `new-${Date.now()}`,
            user: user?.id,
            type: CONTACT_TYPE_EMAIL,
            tier: TIER_PRIVATE,
            verified: false,
            enabled: true,
            contact: '',
            sequence: localContacts.length + 1,
            _isUnsaved: true, // Track that this contact hasn't been saved yet
        };
        const updated = [...localContacts, newContact];
        setLocalContacts(updated);
        updateUserContacts(updated);
        // Start editing the new contact immediately
        setEditingId(newContact.id);
        setEditData({});
    };

    if (!localContacts || localContacts.length === 0) {
        return (
            <Paper elevation={0} className={classes.root}>
                <Box className={classes.emptyState}>
                    <Typography variant="body1" color="textSecondary">
                        No contacts available
                    </Typography>
                    {!readOnly && (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleAddContact}
                            >
                                Add contact
                            </Button>
                            <Tooltip title="Updates from booking systems will not change the primary email">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={user?.freeze_email ?? false}
                                            onChange={(e) => onUpdate({ ...user, freeze_email: e.target.checked })}
                                            color="warning"
                                        />
                                    }
                                    label="Freeze email"
                                    sx={{ ml: 1 }}
                                />
                            </Tooltip>
                            <Tooltip title="Updates from booking systems will not change the primary phone">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={user?.freeze_phone ?? false}
                                            onChange={(e) => onUpdate({ ...user, freeze_phone: e.target.checked })}
                                            color="warning"
                                        />
                                    }
                                    label="Freeze phone"
                                />
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            </Paper>
        );
    }

    return (
        <Box className={classes.root}>
            {confirmDeleteDialog}
            <TableContainer component={Paper} elevation={0} className={classes.tableContainer}>
                <Table size="small">
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell className={classes.tableHeadCell} style={{ width: 50 }}>#</TableCell>
                            <TableCell className={classes.tableHeadCell}>Type</TableCell>
                            <TableCell className={classes.tableHeadCell}>Contact</TableCell>
                            <TableCell className={classes.tableHeadCell}>Tier</TableCell>
                            <TableCell className={classes.tableHeadCell}>OTA</TableCell>
                            {hasWritePerm && <TableCell className={classes.tableHeadCell}></TableCell>}
                            <TableCell className={classes.tableHeadCell} align="center">Verified</TableCell>
                            <TableCell className={classes.tableHeadCell}>Last updated</TableCell>
                            {!readOnly && <TableCell className={classes.tableHeadCell} align="center">Actions</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {localContacts.map((contact, index) => {
                            const contactId = contact.id || index;
                            const isEditing = editingId === contactId;
                            const candidate = isEditing ? { ...contact, ...editData } : contact;
                            const isValidatingPhone = isEditing && Number(candidate.type) === CONTACT_TYPE_PHONE && phoneCheck.checking;
                            const validationError = isEditing
                                ? contactFormatError(candidate.type, candidate.contact) ||
                                  phoneValidationError(candidate.type, candidate.contact) ||
                                  (findDuplicateContact(localContacts, candidate, contact) ? duplicateContactMessage(candidate.type) : '') ||
                                  editError
                                : '';
                            const onlyEmail =
                                contact.type === CONTACT_TYPE_EMAIL &&
                                localContacts.filter((c) => c.type === CONTACT_TYPE_EMAIL).length <= 1;

                            return (
                                <ContactRow
                                    key={contactId}
                                    contact={contact}
                                    index={index}
                                    isEditing={isEditing}
                                    editData={editData}
                                    onEdit={handleEdit}
                                    onSave={() => handleSave(contact)}
                                    onCancel={handleCancel}
                                    onDelete={handleDelete}
                                    onPromoteToPrimary={handlePromoteToPrimary}
                                    onFieldChange={handleFieldChange}
                                    onToggleVerified={handleToggleVerified}
                                    classes={classes}
                                    readOnly={readOnly}
                                    user={user}
                                    hasWritePerm={hasWritePerm}
                                    deleteDisabled={onlyEmail}
                                    deleteDisabledTitle="Cannot delete the only email address"
                                    validationError={validationError}
                                    validating={isValidatingPhone}
                                    saveDisabled={isEditing && (Boolean(validationError) || isValidatingPhone)}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {!readOnly && (
                <Box sx={{ mt: 1, ml: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddContact}
                    >
                        Add contact
                    </Button>
                    <Tooltip title="Updates from booking systems will not change the primary email">
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={user?.freeze_email ?? false}
                                    onChange={(e) => onUpdate({ ...user, freeze_email: e.target.checked })}
                                    color="warning"
                                />
                            }
                            label="Freeze email"
                            sx={{ ml: 1 }}
                        />
                    </Tooltip>
                    <Tooltip title="Updates from booking systems will not change the primary phone">
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={user?.freeze_phone ?? false}
                                    onChange={(e) => onUpdate({ ...user, freeze_phone: e.target.checked })}
                                    color="warning"
                                />
                            }
                            label="Freeze phone"
                        />
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
};

export default UserContactList;
